import React from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import I18n from 'modules/i18n';
import Checkbox from '@/components/common/libraries/common/Checkbox';
import stripStandardsAliasPrefix from '@/utils/stripStandardsAliasPrefix';
import { StandardNode } from '@/components/shared/Standards/types';
import { CheckboxState } from '../standardsFilterUtils';
import RowChevron from '../RowChevron';
import { truncationChildrenAttrs, truncationRowAttrs } from '../TruncatedCorrelationsList';
import StandardLabel from './StandardLabel';

export const INDENT_PX = 16;
export const CHEVRON_PX = 20;

// Same locale fallback as TagCard on this page: EdGate ships Spanish text for some standards;
// where it doesn't, fall back to English rather than a blank row.
export const describeStandard = (node: StandardNode) => {
  const spanish = I18n.locale === 'es';
  const value = (spanish ? node.value_es : null) ?? node.value_en;
  const rawDescription = (spanish ? node.description_es : null) ?? node.description_en;

  return {
    depth: Math.max((node.level ?? 1) - 1, 0),
    description: stripStandardsAliasPrefix(rawDescription, node.alias),
    label: node.alias || value || '',
  };
};

interface StandardRowProps {
  checkboxState: CheckboxState;
  children?: React.ReactNode;
  expandable: boolean;
  expanded: boolean;
  node: StandardNode;
  onCheck: (id: number, checked: boolean) => void;
  onToggle: () => void;
}

// One row of the standards filter — the lazy tree and the search results render the same row;
// only where the children come from differs, so they arrive as collapsible content.
const StandardRow = ({ checkboxState, children, expandable, expanded, node, onCheck, onToggle }: StandardRowProps) => {
  const { depth, description, label } = describeStandard(node);

  return (
    <Collapsible.Root asChild open={expandable && expanded}>
      <li>
        <div
          className="tw-group tw-flex tw-items-start tw-gap-1 tw-py-1 tw-pr-2 hover:tw-bg-gray-50 body sm"
          style={{ paddingLeft: `${depth * INDENT_PX}px` }}
          {...truncationRowAttrs({ checked: checkboxState !== 'unchecked' })}
        >
          {expandable ? (
            <RowChevron expanded={expanded} label={label} onToggle={onToggle} />
          ) : (
            <span aria-hidden className="tw-flex-shrink-0 tw-w-4" />
          )}

          <Checkbox
            shouldCover
            aria-label={`${label} ${description}`.trim()}
            checked={checkboxState === 'checked'}
            extraClassName="tw-items-start tw-flex-1 tw-min-w-0"
            indeterminate={checkboxState === 'indeterminate'}
            label={<StandardLabel alias={label} description={description} />}
            onChange={event => onCheck(node.id, event.target.checked)}
          />
        </div>

        <Collapsible.Content asChild {...truncationChildrenAttrs()}>
          <ul role="list">{children}</ul>
        </Collapsible.Content>
      </li>
    </Collapsible.Root>
  );
};

export default StandardRow;
